import i18n from 'helpers/i18n';
import messages from './messages';
import DisplayVideos from './DisplayVideos';
import VideoNavigation from './VideoNavigation';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { CodingVideo } from 'types/api/TestAttempt';
import { IconButton, Text } from '@teachstonellc/component-library';
import {
  ChevronLeft,
  ChevronRight
} from '@teachstonellc/component-library/dist/icons';

// aria-disabled keeps the arrows focusable at either end of the carousel,
// so these mirror IconButton's own `disabled:` styles.
const ARROW_CLASSES =
  'self-center aria-disabled:!text-ds-icon-disabled aria-disabled:!bg-transparent aria-disabled:cursor-not-allowed';

interface CarouselProps {
  title: string;
  videos: CodingVideo[];
  testTemplateId: number;
}

function Carousel({ title, videos, testTemplateId }: CarouselProps) {
  const [slidesPerPage, setSlidesPerPage] = useState(1);
  const [trackWidth, setTrackWidth] = useState(0);
  // Track the first slide of the current page rather than the page number,
  // so the same videos stay in view when a resize changes slidesPerPage.
  const [anchorSlide, setAnchorSlide] = useState(0);
  const [hasNavigated, setHasNavigated] = useState(false);
  const sliderContainer = useRef<HTMLDivElement>(null);
  const headingId = useId();

  const pageCount = Math.max(1, Math.ceil(videos.length / slidesPerPage));
  const currentPage = Math.min(
    Math.floor(anchorSlide / slidesPerPage),
    pageCount - 1
  );
  // The last page is aligned to the end so it is always full and every
  // video is fully in view on at least one page.
  const firstVisible = Math.max(
    0,
    Math.min(currentPage * slidesPerPage, videos.length - slidesPerPage)
  );
  const lastVisible = Math.min(videos.length, firstVisible + slidesPerPage) - 1;
  const visibleSlides = videos.map(
    (_, idx) => idx >= firstVisible && idx <= lastVisible
  );

  useLayoutEffect(() => {
    const container = sliderContainer.current;

    if (!container) {
      return;
    }

    function measure() {
      const slide = container!.querySelector<HTMLElement>(
        '[data-carousel-slide]'
      );

      if (!slide?.parentElement || slide.offsetWidth === 0) {
        return;
      }

      // Always keep at least one slide accessible, even when it is wider
      // than the carousel (small screens or high zoom).
      const width = slide.parentElement.clientWidth;
      setTrackWidth(width);
      setSlidesPerPage(Math.max(1, Math.floor(width / slide.offsetWidth)));

      // If a video has focus, keep its page in view so reflow never hides
      // (and makes inert) the control the user is on.
      const focusedSlide = document.activeElement?.closest(
        '[data-carousel-slide]'
      );
      const slides = Array.from(
        container!.querySelectorAll('[data-carousel-slide]')
      );
      const focusedIndex = focusedSlide ? slides.indexOf(focusedSlide) : -1;

      if (focusedIndex >= 0) {
        setAnchorSlide(focusedIndex);
      }
    }

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);

    return () => observer.disconnect();
  }, [videos.length]);

  useEffect(() => {
    const container = sliderContainer.current;
    const slides =
      container?.querySelectorAll<HTMLElement>('[data-carousel-slide]') ?? [];

    if (!container || slides.length === 0) {
      return;
    }

    // Reapply on resize too: the browser clamps scrollLeft when the track
    // narrows, which can clip the last slide without changing the page.
    container.scrollLeft =
      slides[firstVisible].offsetLeft - slides[0].offsetLeft;
  }, [firstVisible, trackWidth]);

  function goToPage(index: number) {
    setAnchorSlide(index * slidesPerPage);
    setHasNavigated(true);
  }

  function previousPage() {
    if (currentPage > 0) {
      goToPage(currentPage - 1);
    }
  }

  function nextPage() {
    if (currentPage < pageCount - 1) {
      goToPage(currentPage + 1);
    }
  }

  if (videos.length === 0) {
    return null;
  }

  return (
    <section
      aria-roledescription={i18n.ft(messages.carouselText)}
      aria-labelledby={headingId}
    >
      <Text as="h3" id={headingId} className="mt-8 mb-3 py-2 px-3 w-fit">
        {title}
      </Text>
      <div className="flex items-baseline">
        <IconButton
          icon={<ChevronLeft />}
          size="medium"
          className={ARROW_CLASSES}
          aria-disabled={currentPage === 0}
          onClick={previousPage}
          aria-label={i18n.ft(messages.previousSlide)}
        />
        <div
          ref={sliderContainer}
          className="mx-2 overflow-hidden scroll-smooth"
          aria-live="polite"
        >
          {hasNavigated && (
            <p className="sr-only">
              {i18n.ft(messages.visibleSlides, {
                first: firstVisible + 1,
                last: lastVisible + 1,
                total: videos.length
              })}
            </p>
          )}
          <div className="lg:m-1">
            <div className="rounded-b-xl">
              <div className="relative w-full snap-mandatory snap-x flex">
                <DisplayVideos
                  videos={videos}
                  visibleSlides={visibleSlides}
                  testTemplateId={testTemplateId}
                />
              </div>
            </div>
          </div>
        </div>
        <IconButton
          icon={<ChevronRight />}
          size="medium"
          className={ARROW_CLASSES}
          aria-disabled={currentPage >= pageCount - 1}
          onClick={nextPage}
          aria-label={i18n.ft(messages.nextSlide)}
        />
      </div>
      <div className="text-center">
        <VideoNavigation
          sliderLength={pageCount}
          currentVideoIndex={currentPage}
          onSelect={goToPage}
        />
      </div>
    </section>
  );
}

export default Carousel;
